import React, { useState, useEffect } from "react";

// ─── SETUP ───────────────────────────────────────────────────────────────────
const useSetup = () => {
  useEffect(() => {
    const f = document.createElement("link");
    f.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600;700&display=swap";
    f.rel = "stylesheet"; document.head.appendChild(f);
    const s = document.createElement("style");
    s.textContent = `
      @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
      @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      @keyframes slideIn{from{opacity:0;transform:translateX(-12px)}to{opacity:1;transform:translateX(0)}}
      @keyframes barGrow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
      @keyframes lineGrow{from{stroke-dashoffset:2000}to{stroke-dashoffset:0}}
      @keyframes countUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      .cs-root *{box-sizing:border-box;margin:0;padding:0}
      .cs-root::-webkit-scrollbar{width:4px}
      .cs-root::-webkit-scrollbar-track{background:transparent}
      .cs-root::-webkit-scrollbar-thumb{background:#334155;border-radius:10px}
      .cs-root ::selection{background:#dbeafe;color:#1e40af}
    `;
    document.head.appendChild(s);
  }, []);
};

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const P = {
  bg:"#f8fafc", surface:"#fff", surfaceAlt:"#f1f5f9",
  border:"#e2e8f0", borderLight:"#f1f5f9", borderHover:"#cbd5e1",
  ink:"#0f172a", sub:"#475569", muted:"#94a3b8", faint:"#cbd5e1",
  accent:"#2563eb", accentLight:"#dbeafe", accentDark:"#1e40af",
  green:"#16a34a", greenBg:"#f0fdf4", greenBorder:"#bbf7d0",
  red:"#dc2626", redBg:"#fef2f2", redBorder:"#fecaca",
  amber:"#d97706", amberBg:"#fffbeb",
  purple:"#7c3aed", purpleBg:"#f5f3ff",
  cro:"#db2777", croBg:"#fdf2f8",
  heroBg:"#0a1120", heroBorder:"rgba(255,255,255,.06)",
  display:"'Instrument Serif', Georgia, serif",
  sans:"'DM Sans',-apple-system,sans-serif",
  mono:"'JetBrains Mono',monospace",
};

// Image base path: adjust this to match where your case-study-images folder lives
// Default assumes images are served from /case-study-images/
const IMG = "/case-study-images";

// ─── SHARED COMPONENTS ───────────────────────────────────────────────────────

const HeroStats = ({ stats }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
    gap: 1,
    background: P.heroBorder,
    border: `1px solid ${P.heroBorder}`,
    marginTop: 56,
    borderRadius: 14,
    overflow: "hidden",
  }}>
    {stats.map((s, i) => (
      <div key={i} style={{ background: P.heroBg, padding: "28px 24px" }}>
        <div style={{
          fontFamily: P.mono, fontWeight: 700, fontSize: "clamp(28px, 3vw, 40px)",
          color: s.color || "#fff", letterSpacing: "-.02em",
          animation: "countUp .8s both",
        }}>{s.value}</div>
        <div style={{
          fontSize: 12, color: "rgba(255,255,255,.5)",
          textTransform: "uppercase", letterSpacing: ".1em", marginTop: 8,
        }}>{s.label}</div>
      </div>
    ))}
  </div>
);

const SectionBlock = ({ label, children }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    gap: 48,
    marginBottom: 80,
  }}>
    <div style={{
      fontFamily: P.mono, fontSize: 12, letterSpacing: ".14em",
      textTransform: "uppercase", color: P.muted,
      borderTop: `1px solid ${P.border}`, paddingTop: 16,
    }}>{label}</div>
    <div>{children}</div>
  </div>
);

const H3 = ({ children, accent = P.accent }) => (
  <h3 style={{
    fontFamily: P.display, fontWeight: 400, fontSize: 36,
    lineHeight: 1.15, letterSpacing: "-.02em", marginBottom: 16,
    color: P.ink,
  }}>
    {typeof children === "string" ? children : children}
  </h3>
);

const Para = ({ children }) => (
  <p style={{ fontSize: 17, lineHeight: 1.65, color: P.sub, marginBottom: 16 }}>{children}</p>
);

const Checklist = ({ items }) => (
  <ul style={{ listStyle: "none", padding: 0, margin: "16px 0" }}>
    {items.map((item, i) => (
      <li key={i} style={{
        paddingLeft: 24, position: "relative",
        fontSize: 16, color: P.sub, marginBottom: 10,
      }}>
        <span style={{
          position: "absolute", left: 0, top: 0,
          color: P.green, fontWeight: 700,
        }}>✓</span>
        {item}
      </li>
    ))}
  </ul>
);

const ImageFrame = ({ src, caption, dark = false }) => (
  <div style={{
    background: dark ? "#1e293b" : P.surface,
    border: `1px solid ${dark ? "#334155" : P.border}`,
    borderRadius: 14, padding: 16, margin: "32px 0", overflow: "hidden",
  }}>
    <img src={src} alt={caption} style={{
      width: "100%", height: "auto", display: "block", borderRadius: 8,
    }} />
    {caption && (
      <div style={{
        fontFamily: P.mono, fontSize: 11, letterSpacing: ".08em",
        color: dark ? "rgba(255,255,255,.5)" : P.muted,
        marginTop: 12, textTransform: "uppercase",
      }}>{caption}</div>
    )}
  </div>
);

const KeywordTable = ({ title, source, rows }) => (
  <div style={{
    background: P.surface, border: `1px solid ${P.border}`,
    borderRadius: 14, overflow: "hidden", margin: "32px 0",
  }}>
    <div style={{
      background: P.surfaceAlt, padding: "16px 24px",
      borderBottom: `1px solid ${P.border}`,
      display: "flex", justifyContent: "space-between", alignItems: "center",
    }}>
      <div style={{
        fontFamily: P.mono, fontSize: 12, letterSpacing: ".1em",
        textTransform: "uppercase", color: P.muted,
      }}>{title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: P.ink }}>
        <span style={{ color: P.purple }}>●</span> Source: {source}
      </div>
    </div>
    <div style={{
      display: "grid", gridTemplateColumns: "2fr 80px 80px 100px 100px",
      padding: "12px 24px", borderBottom: `1px solid ${P.borderLight}`,
      background: P.surfaceAlt, fontFamily: P.mono, fontSize: 11,
      letterSpacing: ".1em", textTransform: "uppercase", color: P.muted,
    }}>
      <div>Keyword</div><div>Pos.</div><div>Volume</div><div>Traffic</div><div>Trend</div>
    </div>
    {rows.map((r, i) => (
      <div key={i} style={{
        display: "grid", gridTemplateColumns: "2fr 80px 80px 100px 100px",
        padding: "14px 24px", borderBottom: i === rows.length - 1 ? "none" : `1px solid ${P.borderLight}`,
        fontSize: 14, alignItems: "center",
      }}>
        <div style={{ fontWeight: 500, color: P.ink }}>{r.kw}</div>
        <div style={{
          fontFamily: P.mono, fontWeight: 700,
          color: r.pos === 1 ? P.amber : P.ink,
        }}>#{r.pos}</div>
        <div style={{ fontFamily: P.mono, color: P.sub }}>{r.vol}</div>
        <div style={{ fontFamily: P.mono, color: P.accent, fontWeight: 600 }}>{r.traffic}</div>
        <div style={{
          height: 6, background: P.borderLight, borderRadius: 3, overflow: "hidden",
        }}>
          <div style={{
            height: "100%",
            background: `linear-gradient(90deg, ${P.accent}, ${P.accentDark})`,
            borderRadius: 3, transformOrigin: "left",
            animation: "barGrow 1s both ease-out",
            width: `${r.barPct}%`,
          }} />
        </div>
      </div>
    ))}
  </div>
);

const BeforeAfter = ({ before, after }) => (
  <div style={{
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 24, margin: "32px 0",
  }}>
    {[before, after].map((b, i) => (
      <div key={i} style={{
        background: P.surface, border: `1px solid ${P.border}`,
        borderRadius: 14, overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px", display: "flex",
          justifyContent: "space-between", alignItems: "center",
          background: i === 0 ? P.redBg : P.greenBg,
          borderBottom: `1px solid ${P.border}`,
        }}>
          <div style={{
            fontFamily: P.mono, fontSize: 11, letterSpacing: ".12em",
            textTransform: "uppercase", fontWeight: 700,
            color: i === 0 ? P.red : P.green,
          }}>{i === 0 ? `Before · ${b.date}` : `After · ${b.date}`}</div>
          <div style={{
            fontFamily: P.mono, fontWeight: 700, fontSize: 18,
            color: i === 0 ? P.red : P.green,
          }}>Position #{b.pos}</div>
        </div>
        <img src={b.img} alt={`${b.date} ranking`} style={{
          width: "100%", height: "auto", display: "block", background: "#fff",
        }} />
      </div>
    ))}
  </div>
);

const Testimonial = ({ quote, author }) => (
  <div style={{
    background: P.heroBg, color: "#fff",
    padding: "80px 32px", textAlign: "center",
  }}>
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <blockquote style={{
        fontFamily: P.display, fontStyle: "italic", fontWeight: 400,
        fontSize: "clamp(28px, 4vw, 44px)", lineHeight: 1.25,
        letterSpacing: "-.015em", color: "#fff",
      }}>
        <span style={{ color: P.cro, fontSize: "1.2em" }}>"</span>
        {quote}
        <span style={{ color: P.cro, fontSize: "1.2em" }}>"</span>
      </blockquote>
      <div style={{
        marginTop: 32, fontFamily: P.mono, fontSize: 13,
        letterSpacing: ".08em", textTransform: "uppercase",
        color: "rgba(255,255,255,.6)",
      }}>
        {author}
      </div>
    </div>
  </div>
);

const CaseHero = ({ industry, title, gradientStart, gradientEnd, lede, stats, customHeader }) => (
  <div style={{
    background: P.heroBg, color: "#fff",
    padding: "100px 32px 80px",
  }}>
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div style={{
        fontFamily: P.mono, fontSize: 12, letterSpacing: ".14em",
        textTransform: "uppercase", color: "rgba(255,255,255,.5)", marginBottom: 20,
      }}>{industry}</div>
      {customHeader}
      <h2 style={{
        fontFamily: P.display, fontWeight: 400,
        fontSize: "clamp(48px, 6vw, 80px)", lineHeight: 1, letterSpacing: "-.025em",
        maxWidth: 900,
      }}>
        {title.split(/(<em>.*?<\/em>)/g).map((part, i) => {
          const m = part.match(/^<em>(.*?)<\/em>$/);
          if (m) return (
            <em key={i} style={{
              fontStyle: "italic", color: "transparent",
              background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`,
              WebkitBackgroundClip: "text", backgroundClip: "text",
            }}>{m[1]}</em>
          );
          return part;
        })}
      </h2>
      <p style={{
        color: "rgba(255,255,255,.75)", maxWidth: 720, marginTop: 24,
        fontSize: 18, lineHeight: 1.6,
      }}>{lede}</p>
      <HeroStats stats={stats} />
    </div>
  </div>
);

const CaseBody = ({ children }) => (
  <div style={{ background: P.bg, padding: "80px 32px" }}>
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>{children}</div>
  </div>
);

// ─── INDEX / GRID ────────────────────────────────────────────────────────────

const CaseCard = ({ flagship, livePill, industry, title, accent, stats, onClick }) => (
  <a onClick={onClick} style={{
    background: flagship ? P.heroBg : P.surface,
    color: flagship ? "#fff" : P.ink,
    border: flagship ? "1px solid rgba(255,255,255,.08)" : `1px solid ${P.border}`,
    borderRadius: 16, padding: 28, textDecoration: "none",
    transition: "all .2s", cursor: "pointer",
    display: "flex", flexDirection: "column",
    animation: "fadeUp .6s both",
    gridColumn: flagship ? "span 2" : "auto",
  }}>
    {(flagship || livePill) && (
      <div style={{
        display: "inline-block", padding: "4px 10px", borderRadius: 999,
        background: "linear-gradient(135deg, #db2777, #7c3aed)",
        color: "#fff", fontSize: 10, fontWeight: 700, letterSpacing: ".1em",
        textTransform: "uppercase", marginBottom: 14, alignSelf: "flex-start",
      }}>{flagship ? "Flagship" : livePill}</div>
    )}
    <div style={{
      fontFamily: P.mono, fontSize: 11, letterSpacing: ".12em",
      textTransform: "uppercase", marginBottom: 16,
      color: flagship ? "rgba(255,255,255,.5)" : P.muted,
    }}>{industry}</div>
    <h3 style={{
      fontFamily: P.display, fontWeight: 400,
      fontSize: flagship ? 44 : 32, lineHeight: 1.1,
      letterSpacing: "-.02em", marginBottom: 14,
    }}>{title}</h3>
    <div style={{
      display: "flex", gap: 24, flexWrap: "wrap",
      marginTop: "auto", paddingTop: 24,
      borderTop: flagship ? "1px solid rgba(255,255,255,.08)" : `1px solid ${P.border}`,
    }}>
      {stats.map((s, i) => (
        <div key={i}>
          <div style={{
            fontFamily: P.mono, fontWeight: 700, fontSize: 22,
            color: flagship ? "#fff" : P.accent,
          }}>{s.v}</div>
          <div style={{
            fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em",
            marginTop: 2, color: flagship ? "rgba(255,255,255,.5)" : P.muted,
          }}>{s.l}</div>
        </div>
      ))}
    </div>
    <div style={{
      color: flagship ? P.cro : P.accent, fontSize: 14, fontWeight: 600,
      marginTop: 12,
    }}>Read the case study →</div>
  </a>
);

// ─── INDIVIDUAL CASE STUDIES ─────────────────────────────────────────────────

const PlasticSurgeryCase = () => (
  <section>
    <CaseHero
      industry="Case Study #01 · Plastic Surgery"
      title='Ranked #1 on Google for <em>"best plastic surgeon in the world."</em> Then 8,000%+ traffic growth.'
      gradientStart="#db2777" gradientEnd="#7c3aed"
      lede='Premier Beverly Hills plastic surgery practice. The campaign produced #1 Google rankings for the most competitive plastic-surgery keywords on the open web, including "best plastic surgeon in the world," "plastic surgeon Beverly Hills," "tummy tuck Beverly Hills," "tummy tuck cost," and the #1 image rank for "tummy tuck." Organic traffic moved from 1,720 visits/month in January 2022 to a peak of 139,418 visits/month, an 8,000%+ increase verified across Ahrefs and Google Analytics.'
      stats={[
        { value: "#1 Ranking", label: '"Best Plastic Surgeon..."' },
        { value: "+8,000%", label: "Organic Traffic Growth", color: "#22c55e" },
        { value: "1,720 → 139K", label: "Monthly Visitors" },
        { value: "$108K/mo", label: "Organic Traffic Value", color: "#fbbf24" },
      ]}
    />
    <CaseBody>
      <SectionBlock label="The Problem">
        <H3>A world-class surgeon. A site nobody could find.</H3>
        <Para>The practice was an established Beverly Hills name with a strong brand offline: magazine features, press, a celebrity clientele. Online, the picture was different. The site ranked #41 for its single most important commercial keyword, sat outside the top 10 for every revenue procedure, and pulled in fewer than 60 organic visitors per day.</Para>
        <Para>Competition for plastic-surgery keywords in Los Angeles is among the hardest in any vertical. Competitors had years of authority, heavy press coverage, and aggressive paid budgets. The path to #1 wasn't going to be more content or more links; it was going to be tighter execution across every layer of the site.</Para>
      </SectionBlock>

      <SectionBlock label="The Solution">
        <H3>Procedure-page-first content strategy with surgical execution.</H3>
        <Para>The campaign rebuilt the site's procedure pages around a single thesis: <em>each procedure page must be the most authoritative resource on the open web for that procedure.</em> No thin content, no boilerplate. Real expertise written by the surgeon, supported by case data, structured for both rankings and conversions.</Para>
        <Checklist items={[
          "Comprehensive technical audit and remediation across 290+ pages",
          "Full procedure-page rebuild: long-form expert content backed by the surgeon's own case data",
          "Topic-cluster blog architecture: every commercial procedure surrounded by supporting informational content",
          "Schema implementation across procedures, FAQs, and reviews",
          "Continuous content refresh on aging pages to defend rankings",
          "Foundational link building across high-authority medical and lifestyle publications",
        ]} />
      </SectionBlock>

      <SectionBlock label="Six-Month Result">
        <H3>From #41 to #1 on the hardest commercial term in the vertical.</H3>
        <Para>Within six months, the site moved from page 5 to page 1, and within seven months it held the #1 organic position for "plastic surgeon Beverly Hills", one of the most competitive local-commercial keywords on Google. The position has held since.</Para>
        <BeforeAfter
          before={{ date: "Jan 10, 2022", pos: 41, img: `${IMG}/dc_rank_before.png` }}
          after={{ date: "Jul 7, 2022", pos: 1, img: `${IMG}/dc_rank_after.png` }}
        />
        <Para>The same trajectory played out across every commercial procedure on the site. Within twelve months, the practice held top-3 positions for procedure terms competitors had owned for years.</Para>
      </SectionBlock>

      <SectionBlock label="Top Rankings">
        <H3>#1 across the highest-value plastic-surgery keywords on the open web.</H3>
        <Para>The site captured the #1 organic position on Google for the most commercially valuable and competitive plastic-surgery search terms in the world, including the global #1 for "best plastic surgeon in the world" and the #1 image rank for "tummy tuck," a position retained continuously.</Para>
        <KeywordTable
          title="Top Organic Rankings · #1 Keywords"
          source="Google / Ahrefs"
          rows={[
            { kw: "best plastic surgeon in the world", pos: 1, vol: "Global", traffic: "flagship", barPct: 100 },
            { kw: "plastic surgeon Beverly Hills", pos: 1, vol: "Local", traffic: "primary", barPct: 92 },
            { kw: "tummy tuck Beverly Hills", pos: 1, vol: "Local", traffic: "commercial", barPct: 84 },
            { kw: "tummy tuck cost", pos: 1, vol: "32K", traffic: "19,391", barPct: 96 },
            { kw: "tummy tuck (image)", pos: 1, vol: "88K", traffic: "image rank", barPct: 100 },
          ]}
        />
        <Para style={{ fontFamily: P.mono, fontSize: 12, color: P.muted, textTransform: "uppercase", letterSpacing: ".08em", textAlign: "center", marginTop: -16 }}>
          + many more high-volume buyer-intent keywords across the procedure catalog · 34,100 ranking keywords site-wide
        </Para>
      </SectionBlock>

      <SectionBlock label="Traffic Growth · Verified">
        <H3>1,720 monthly visits to 139,418, verified independently.</H3>
        <Para>The growth held across every measurement source: Ahrefs organic-traffic estimates, Google Search Console, and Google Analytics 4. Below is the Ahrefs five-year view, capturing the full inflection from January 2022 onward.</Para>
        <ImageFrame dark src={`${IMG}/dc_ahrefs_5y.png`} caption="Ahrefs · 1,425 starting point · 150K+ peak organic visitors/month" />
        <ImageFrame dark src={`${IMG}/dc_ahrefs_backlink_2y.png`} caption="Ahrefs · DR 45 · 3.4K backlinks · 882 referring domains · 43.7K organic keywords · 139K organic traffic ($108K value)" />
        <Para>Google Analytics confirmed the same trajectory: organic search became the dominant channel by a factor of 4x over paid, growing user counts from low five-figures to over 668K total users with 376K from organic search alone over the campaign window.</Para>
        <ImageFrame src={`${IMG}/dc_ga_traffic.png`} caption="Google Analytics 4 · Daily Visits Organic Search · Organic Search Dominates Total User Growth" />
      </SectionBlock>

      {/* FE1 (2026-10-28, the PM's draft H): this was client work before SerpDesk; the claim that it is "now automated"
          with tools customers cannot use (Tech SEO, Link Intelligence, SERP Decisions) is gone */}
      <SectionBlock label="Methodology">
        <Para>Client work by Andrew Suver at Vantage Digital, before SerpDesk existed. SerpDesk did not produce these results; its tools follow the same approach.</Para>
      </SectionBlock>
    </CaseBody>
  </section>
);

const TripLogCase = () => (
  <section>
    <CaseHero
      industry="Case Study #02 · SaaS / Mobile App"
      title="The <em>highest-ranked</em> mileage tracker app in the world."
      gradientStart="#2563eb" gradientEnd="#3b82f6"
      lede="After a website redesign in late 2018, TripLog was bleeding rankings and organic traffic. Twelve months later, TripLog held #1 positions across the most competitive mileage-tracking keywords on Google, outranking every category competitor including the largest established players in the space."
      stats={[
        { value: "#1", label: '"TripLog"' },
        { value: "#2", label: '"Mileage Tracker"' },
        { value: "#2", label: '"Mileage App"' },
        { value: "5.3K", label: "Ranking Keywords" },
      ]}
      customHeader={
        <div style={{ marginBottom: 24 }}>
          <div style={{
            background: "#fff", padding: "10px 16px", borderRadius: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,.4)",
            display: "inline-block",
          }}>
            <img src={`${IMG}/triplog_logo.svg`} alt="TripLog" style={{ height: 32, display: "block" }} />
          </div>
        </div>
      }
    />
    <CaseBody>
      <SectionBlock label="The Client">
        <H3>TripLog Mileage: mileage tracker app and expense reimbursement cloud.</H3>
        <Para>TripLog is a mileage tracking and expense reimbursement platform serving mobile workers, small business owners, and corporate fleets. The product had a strong feature set and a loyal customer base, but search visibility had become a problem after a major site redesign.</Para>
      </SectionBlock>

      <SectionBlock label="The Problem">
        <H3>A redesign that quietly broke the rankings.</H3>
        <Para>Near the end of 2018, TripLog began bleeding rankings on its most important commercial keywords. After a full website redesign, the site experienced the common but punishing problem of a content and structural reset: URL changes, internal-link disruption, lost on-page signals, content that no longer mapped to ranking pages. Organic traffic was declining month over month and the team needed an aggressive recovery strategy.</Para>
      </SectionBlock>

      <SectionBlock label="The Solution">
        <H3>Full-stack SEO recovery with a heavy link-building focus.</H3>
        <Para>The campaign opened with competitive analysis, in-depth keyword research, and a comprehensive site audit. The audit surfaced the structural issues holding the site back. From there:</Para>
        <Checklist items={[
          "Technical SEO audit and remediation across the full site",
          "Comprehensive backlink analysis and toxic-link cleanup",
          "Site-wide on-page optimization aligned to commercial keyword intent",
          "Foundational link building through original blog content and direct outreach",
          "Continuous on-page refinement as rankings climbed",
        ]} />
        <Para>The first month closed the on-page gap. Months two through twelve focused almost entirely on authority: building the backlink profile required to outrank category incumbents.</Para>
      </SectionBlock>

      <SectionBlock label="Six-Month Result">
        <H3>From page 2 to top-5 on every commercial keyword.</H3>
        <Para>After six months, TripLog had moved from second-page rankings to top-5 positions across its most competitive commercial keywords. Organic traffic had recovered and surpassed pre-redesign levels.</Para>
      </SectionBlock>

      <SectionBlock label="12-Month Result">
        <H3>The highest organically-ranked mileage app on Google.</H3>
        <Para>After twelve months, TripLog held the #1 organic position for its branded keyword and #2 positions on the two most competitive commercial keywords in the entire mileage-tracking category, outranking every other mileage app on Google, including the largest established players in the space.</Para>
        <KeywordTable
          title="Top Organic Keywords · TripLog Mileage"
          source="SEMrush"
          rows={[
            { kw: "mileage tracker", pos: 2, vol: "12,100", traffic: "18.44%", barPct: 100 },
            { kw: "triplog", pos: 1, vol: "1,900", traffic: "17.82%", barPct: 96 },
            { kw: "mileage tracker app", pos: 3, vol: "6,600", traffic: "6.96%", barPct: 38 },
            { kw: "trip log", pos: 1, vol: "1,000", traffic: "5.51%", barPct: 30 },
            { kw: "mileage app", pos: 2, vol: "2,900", traffic: "4.42%", barPct: 24 },
            { kw: "mileage from", pos: 11, vol: "6,600", traffic: "3.63%", barPct: 20 },
            { kw: "trip log mileage", pos: 1, vol: "320", traffic: "3.00%", barPct: 16 },
          ]}
        />
        <Para>In total, the site captured organic visibility on 5,300+ keywords, most driven by content and authority work executed during the engagement.</Para>

        <ImageFrame
          src={`${IMG}/triplog_keywords.png`}
          caption="SEMrush · TripLog Mileage · Top Organic Keywords · #1 for branded terms, #2 for category terms"
        />
      </SectionBlock>
    </CaseBody>
    <Testimonial
      quote="The campaign took TripLog from declining post-redesign to the highest organically-ranked mileage app on Google."
      author={<>CASE STUDY · <strong style={{ color: "#fff" }}>TripLog Mileage</strong> · Outranked every category competitor in 12 months</>}
    />
  </section>
);

const SkincareCase = () => (
  <section>
    <CaseHero
      industry="Case Study #03 · DTC E-commerce / Skincare"
      title="Three years of stagnation, then a <em>47x organic explosion</em>."
      gradientStart="#16a34a" gradientEnd="#22c55e"
      lede="A direct-to-consumer skincare brand had grown organic traffic to fewer than 200 visits per month over four years of effort. After ten months of content-first SEO, organic traffic broke 9,700 visits per month, a 47x increase from the pre-campaign baseline, driven by #1 rankings on high-intent buyer keywords directly tied to product purchases."
      stats={[
        { value: "47x", label: "Organic Traffic Growth", color: "#22c55e" },
        { value: "205 → 9,778", label: "Monthly Visitors" },
        { value: "#1", label: "Top Buyer-Intent Term" },
        { value: "10 mo", label: "Time to Result" },
      ]}
    />
    <CaseBody>
      <SectionBlock label="The Problem">
        <H3>A great product. A flat-line traffic chart.</H3>
        <Para>The brand had a strong product and a healthy paid-social engine. Organic search was effectively dead: the site had been online for nearly four years and was generating fewer than 200 monthly organic visits despite ranking for thousands of keywords (none commercially valuable). The product pages were thin. The blog was unfocused. Internal links pointed in every direction except toward conversion pages.</Para>
      </SectionBlock>

      <SectionBlock label="The Solution">
        <H3>Education-first content built around buyer intent.</H3>
        <Para>Skincare buyers research heavily before they buy. The campaign reorganized the content strategy around the questions buyers actually ask in the discovery phase (specific concerns, ingredient pairings, treatment comparisons) and pointed every article at the relevant product page through structured internal links.</Para>
        <Checklist items={[
          "Topic-cluster blog architecture covering high-intent skincare questions",
          "Long-form expert articles built to outrank existing top-3 results",
          "Internal linking from blog content into commercial product pages",
          "Schema markup for products, FAQs, and HowTo content",
          "Continuous freshness updates as articles climbed",
        ]} />
      </SectionBlock>

      <SectionBlock label="The Result">
        <H3>From 205 to 9,778 organic visits, a 47x lift in ten months.</H3>
        <Para>Organic traffic moved from a multi-year flat line to a vertical line. The breakout happened around month four of the campaign, when articles built earlier in the engagement reached top-3 positions and began compounding traffic into product pages.</Para>
        <ImageFrame dark src={`${IMG}/mdg_ahrefs_chart.png`} caption="Ahrefs · Five-Year Organic Performance · 205 → 9,778 monthly organic visits (Aug 2022 → Nov 2023)" />
        <Para>The growth wasn't just more visits; it was the right visits. Top organic pages held #1 positions for high-volume buyer-intent keywords directly tied to product purchases.</Para>
        <KeywordTable
          title="Top Organic Pages · Buyer-Intent Skincare Keywords"
          source="Ahrefs"
          rows={[
            { kw: "furrowed brow (treatment)", pos: 1, vol: "10K", traffic: "2,761", barPct: 100 },
            { kw: "how to fix under-eye hollows without filler", pos: 1, vol: "400", traffic: "1,412", barPct: 70 },
            { kw: "can I use glycolic acid with vitamin C", pos: 1, vol: "250", traffic: "1,066", barPct: 55 },
            { kw: "orange peel skin", pos: 5, vol: "1.9K", traffic: "737", barPct: 38 },
            { kw: "how to increase fat under eyes naturally", pos: 1, vol: "700", traffic: "601", barPct: 32 },
          ]}
        />
        <Para>Channel mix shifted accordingly. Google Analytics 4 captured the trajectory: Organic Search grew from a long flat line to the dominant new-user channel for the brand, surpassing both Organic Social and paid acquisition.</Para>
        <ImageFrame src={`${IMG}/mdg_ga_users.png`} caption="Google Analytics 4 · User Acquisition · Organic Search emerges as the dominant channel" />
      </SectionBlock>
    </CaseBody>
  </section>
);

const MotopartsCase = () => (
  <section>
    <CaseHero
      industry="Case Study #04 · DTC E-commerce / Powersports & Motorcycle Parts"
      title="Powersports parts retailer: <em>2.4x organic traffic in four months</em>."
      gradientStart="#d97706" gradientEnd="#f59e0b"
      lede="A direct-to-consumer powersports and motorcycle parts retailer had been growing organic traffic slowly for over a year. After implementing a focused on-page and content optimization strategy, organic visits moved from 20,000/month to 48,000/month in four months, a 2.4x lift on an already-mature site."
      stats={[
        { value: "2.4x", label: "Organic Visit Growth", color: "#fbbf24" },
        { value: "20K → 48K", label: "Monthly Visitors" },
        { value: "4 mo", label: "Time to Result" },
        { value: "+140%", label: "Total Lift" },
      ]}
    />
    <CaseBody>
      <SectionBlock label="The Problem">
        <H3>A mature site with slowing growth.</H3>
        <Para>The site was mature, well-stocked, and ranking for thousands of long-tail product keywords. But growth had plateaued: adding new products or content yielded marginal gains, and the high-volume commercial keywords were stuck on page 2 or 3 against larger competitors. The team needed a way to break the plateau without rebuilding the catalog.</Para>
      </SectionBlock>

      <SectionBlock label="The Solution">
        <H3>Category-page optimization and intent-driven content gap closure.</H3>
        <Para>The campaign focused on the highest-ROI surface area: category pages. Most powersports parts retailers treat category pages as filtered product grids with no content. The campaign rebuilt them as the most authoritative buyer-resource pages on the open web for each category: buying guides, fitment information, comparison content, and structured data marking up the entire product set.</Para>
        <Checklist items={[
          "Category-page content rebuild across the highest-volume buyer queries",
          "Internal-linking restructure to push authority from blog to category to product",
          "Content-gap analysis surfacing thousands of unranked buyer-intent queries",
          "Schema implementation across products, categories, FAQs, and reviews",
          "Foundational link building targeting industry and enthusiast publications",
        ]} />
      </SectionBlock>

      <SectionBlock label="The Result">
        <H3>20,000 to 48,000 monthly organic visits in four months.</H3>
        <Para>The campaign produced a 2.4x lift on top of a mature traffic base, meaningfully harder to do than 2.4x on a small site. The trajectory was clean: a flat baseline through the audit and optimization phase, followed by a sharp inflection as category pages took hold.</Para>

        <ImageFrame
          src={`${IMG}/solomoto_ga.png`}
          caption="Google Analytics · Organic Search · 20,000 → 48,000 monthly visits in ~4 months · Apr 2017 – Jul 2018"
        />

        {/* Recreated growth chart */}
        <div style={{
          background: P.heroBg, border: `1px solid ${P.heroBorder}`,
          borderRadius: 14, padding: 32, margin: "32px 0",
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "flex-start", marginBottom: 24, color: "#fff",
          }}>
            <div>
              <div style={{
                fontFamily: P.mono, fontSize: 12, letterSpacing: ".1em",
                textTransform: "uppercase", color: "rgba(255,255,255,.5)", marginBottom: 6,
              }}>Google Analytics · Organic Search · Users</div>
              <h4 style={{
                fontFamily: P.display, fontWeight: 400, fontSize: 28,
                letterSpacing: "-.01em",
              }}>Organic Visit Growth</h4>
            </div>
            <div style={{
              fontFamily: P.mono, fontWeight: 700, fontSize: 24, color: "#22c55e",
            }}>+140%</div>
          </div>
          <svg viewBox="0 0 800 280" preserveAspectRatio="none" style={{ width: "100%", height: 280, display: "block" }} xmlns="http://www.w3.org/2000/svg">
            <line x1="40" y1="40" x2="780" y2="40" stroke="rgba(255,255,255,.05)" strokeWidth="1"/>
            <line x1="40" y1="100" x2="780" y2="100" stroke="rgba(255,255,255,.05)" strokeWidth="1"/>
            <line x1="40" y1="160" x2="780" y2="160" stroke="rgba(255,255,255,.05)" strokeWidth="1"/>
            <line x1="40" y1="220" x2="780" y2="220" stroke="rgba(255,255,255,.05)" strokeWidth="1"/>
            <text x="30" y="44" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="end">50K</text>
            <text x="30" y="104" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="end">37.5K</text>
            <text x="30" y="164" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="end">25K</text>
            <text x="30" y="224" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="end">12.5K</text>
            <text x="80" y="270" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle">Apr 2017</text>
            <text x="240" y="270" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle">Aug 2017</text>
            <text x="400" y="270" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle">Dec 2017</text>
            <text x="560" y="270" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle">Mar 2018</text>
            <text x="720" y="270" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle">Jul 2018</text>
            <defs>
              <linearGradient id="moto-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity=".3"/>
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path d="M 80 130 L 130 128 L 180 135 L 230 132 L 280 130 L 330 128 L 380 132 L 430 145 L 480 152 L 530 155 L 580 130 L 630 90 L 680 50 L 720 50 L 720 240 L 80 240 Z" fill="url(#moto-grad)"/>
            <path d="M 80 130 L 130 128 L 180 135 L 230 132 L 280 130 L 330 128 L 380 132 L 430 145 L 480 152 L 530 155 L 580 130 L 630 90 L 680 50 L 720 50"
                  fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray="2000" strokeDashoffset="0"
                  style={{ animation: "lineGrow 2.5s ease-out" }}/>
            <circle cx="80" cy="130" r="5" fill="#94a3b8"/>
            <line x1="80" y1="130" x2="80" y2="50" stroke="rgba(148,163,184,.3)" strokeWidth="1" strokeDasharray="3,3"/>
            <text x="80" y="40" fill="#94a3b8" fontFamily="JetBrains Mono" fontSize="11" textAnchor="middle" fontWeight="700">20,000</text>
            <text x="80" y="26" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle">BASELINE</text>
            <circle cx="720" cy="50" r="6" fill="#22c55e"/>
            <circle cx="720" cy="50" r="10" fill="#22c55e" opacity="0.2"/>
            <text x="720" y="36" fill="#22c55e" fontFamily="JetBrains Mono" fontSize="13" textAnchor="middle" fontWeight="700">48,000</text>
            <text x="720" y="22" fill="rgba(255,255,255,.4)" fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle">PEAK</text>
            <line x1="530" y1="40" x2="720" y2="40" stroke="#22c55e" strokeWidth="1" opacity="0.4"/>
            <text x="625" y="32" fill="#22c55e" fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle">~ 4 MONTHS</text>
          </svg>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 20 }}>
            <div style={{ color: "rgba(255,255,255,.7)", fontSize: 13, paddingLeft: 12, borderLeft: "2px solid #94a3b8" }}>
              <div style={{ fontFamily: P.mono, fontSize: 11, color: "rgba(255,255,255,.5)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 4 }}>Apr 2017 → Dec 2017</div>
              <div style={{ fontFamily: P.mono, fontWeight: 700, fontSize: 20, color: "#fff" }}>~ 20,000 / month</div>
            </div>
            <div style={{ color: "rgba(255,255,255,.7)", fontSize: 13, paddingLeft: 12, borderLeft: "2px solid #22c55e" }}>
              <div style={{ fontFamily: P.mono, fontSize: 11, color: "rgba(255,255,255,.5)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 4 }}>Apr 2018 → Jul 2018</div>
              <div style={{ fontFamily: P.mono, fontWeight: 700, fontSize: 20, color: "#fff" }}>~ 48,000 / month</div>
            </div>
          </div>
        </div>

        <Para>The pattern that produced this: flat baseline → audit → on-page rebuild → inflection → compounding.</Para>
      </SectionBlock>
    </CaseBody>
    <Testimonial
      quote="The work helped improve our overall marketing ROAS while seeing big increases in organic traffic. Andrew knows his stuff and I highly recommend him."
      author={<><strong style={{ color: "#fff" }}>Daniel Relich</strong> · DTC E-commerce</>}
    />
  </section>
);

// ─── INDEX PAGE ──────────────────────────────────────────────────────────────

const CaseStudiesIndex = ({ onSelect }) => (
  <>
    {/* Page Hero */}
    <section style={{
      background: P.heroBg, color: "#fff",
      padding: "80px 32px 60px", textAlign: "center",
    }}>
      <div style={{
        fontFamily: P.mono, fontSize: 12, letterSpacing: ".12em",
        color: "rgba(255,255,255,.5)", textTransform: "uppercase", marginBottom: 16,
      }}>Proof · 11 Years of SEO</div>
      <h1 style={{
        fontFamily: P.display, fontWeight: 400,
        fontSize: "clamp(48px, 7vw, 88px)", lineHeight: 1, letterSpacing: "-.025em",
        maxWidth: 900, margin: "0 auto",
      }}>
        The work that{" "}
        <em style={{
          fontStyle: "italic", color: "transparent",
          background: "linear-gradient(135deg, #db2777, #7c3aed)",
          WebkitBackgroundClip: "text", backgroundClip: "text",
        }}>built the methodology</em>{" "}
        behind SerpDesk.
      </h1>
      <p style={{
        color: "rgba(255,255,255,.7)", margin: "24px auto 0",
        maxWidth: 640, fontSize: 17, lineHeight: 1.6,
      }}>Client work by Andrew Suver at Vantage Digital, before SerpDesk existed. SerpDesk did not produce these results; its tools follow the same approach.</p>
    </section>

    {/* Attribution */}
    <section style={{
      background: P.heroBg, color: "rgba(255,255,255,.5)",
      padding: "0 32px 60px", textAlign: "center",
      fontSize: 14, lineHeight: 1.7,
      borderBottom: `1px solid ${P.heroBorder}`,
    }}>
      <p>Built by <strong style={{ color: "rgba(255,255,255,.85)" }}>Andrew Suver</strong> · 11 years of SEO consulting.</p>
    </section>

    {/* Grid */}
    <section style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px" }}>
      <h2 style={{
        fontFamily: P.display, fontWeight: 400, fontSize: 56,
        letterSpacing: "-.025em", lineHeight: 1, marginBottom: 8,
      }}>
        Four campaigns. <em style={{ fontStyle: "italic", color: P.accent }}>Four outcomes.</em>
      </h2>
      <div style={{ marginBottom: 48 }} />
      <div style={{
        display: "grid", gap: 24,
        gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
      }}>
        <CaseCard
          flagship
          industry="Plastic Surgery · Beverly Hills"
          title={<>Premier Beverly Hills practice: ranked <em style={{ fontStyle: "italic" }}>#1 on Google</em> for "best plastic surgeon in the world." 8,000%+ traffic growth.</>}
          stats={[
            { v: "#1 Ranking", l: '"Best Plastic Surgeon..."' },
            { v: "8,000%+", l: "Organic Traffic" },
            { v: "#1", l: "Tummy Tuck Image" },
            { v: "$108K/mo", l: "Traffic Value" },
          ]}
          onClick={() => onSelect("plastic-surgery")}
        />
        <CaseCard
          industry="SaaS · Mileage Tracker App"
          title={<>TripLog: highest-ranked mileage app in the world, outranking MileIQ.</>}
          stats={[
            { v: "#1", l: '"TripLog"' },
            { v: "#2", l: '"Mileage Tracker"' },
            { v: "5.3K", l: "Ranking Keywords" },
          ]}
          onClick={() => onSelect("triplog")}
        />
        <CaseCard
          industry="DTC E-commerce · Skincare"
          title="From three years of stagnation to a 47x organic traffic explosion."
          stats={[
            { v: "47x", l: "Organic Traffic" },
            { v: "#1", l: "Buyer-Intent Terms" },
            { v: "9,778", l: "Monthly Visits" },
          ]}
          onClick={() => onSelect("skincare")}
        />
        <CaseCard
          industry="DTC E-commerce · Powersports / Motorcycle Parts"
          title="2.4x organic traffic in four months for a powersports parts retailer."
          stats={[
            { v: "2.4x", l: "Organic Visits" },
            { v: "4 mo", l: "Time to Result" },
            { v: "+140%", l: "Growth" },
          ]}
          onClick={() => onSelect("motoparts")}
        />
        {/* FE1 (the PM's answer, 2026-10-28): the "platform result" card is gone: no source for it */}
      </div>
    </section>
  </>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const CaseStudiesPage = () => {
  useSetup();
  const [view, setView] = useState("index"); // index | plastic-surgery | triplog | skincare | motoparts

  // FE1 (2026-10-28): the platform case (no source) and the "running every night" bridge are gone
  const renderCase = () => {
    switch (view) {
      case "plastic-surgery": return <PlasticSurgeryCase />;
      case "triplog": return <TripLogCase />;
      case "skincare": return <SkincareCase />;
      case "motoparts": return <MotopartsCase />;
      default: return <CaseStudiesIndex onSelect={setView} />;
    }
  };

  return (
    <div className="cs-root" style={{
      background: P.bg, color: P.ink,
      fontFamily: P.sans, fontSize: 15, lineHeight: 1.6,
      WebkitFontSmoothing: "antialiased", minHeight: "100vh",
    }}>
      {/* Internal Header removed: global Layout in App.tsx provides shared nav. */}

      {/* Back button if viewing a case */}
      {view !== "index" && (
        <div style={{
          background: P.heroBg, padding: "12px 32px",
          borderBottom: `1px solid ${P.heroBorder}`,
        }}>
          <a onClick={() => setView("index")} style={{
            color: "rgba(255,255,255,.7)", textDecoration: "none",
            fontSize: 13, fontFamily: P.mono, cursor: "pointer",
          }}>← Back to all case studies</a>
        </div>
      )}

      {renderCase()}

      {/* CTA */}
      <section style={{
        background: "linear-gradient(135deg, #7c3aed, #db2777, #d97706)",
        color: "#fff", padding: "100px 32px", textAlign: "center",
      }}>
        <h2 style={{
          fontFamily: P.display, fontWeight: 400,
          fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1.05,
          letterSpacing: "-.02em", maxWidth: 800, margin: "0 auto",
        }}>
          See what SerpDesk would fix first on{" "}
          <em style={{ fontStyle: "italic" }}>your site</em>.
        </h2>
        <p style={{
          color: "rgba(255,255,255,.85)", marginTop: 20,
          maxWidth: 540, marginLeft: "auto", marginRight: "auto", fontSize: 17,
        }}>Book a demo and we will walk through your site's first reading with you.</p>
        <a
          href="https://calendly.com/andrew-suver"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block", marginTop: 36,
            background: "#fff", color: P.ink,
            padding: "18px 36px", borderRadius: 999,
            textDecoration: "none", fontWeight: 600, fontSize: 16,
          }}
        >Book a demo</a>
      </section>

      {/* Internal Footer removed: global Layout in App.tsx provides shared footer. */}
    </div>
  );
};

export default CaseStudiesPage;
